import DevTools from "@/components/devtools";
import Wrapper from "@/components/layout/wrapper";
import { StaffSessionProvider, useStaffSession } from "@/contexts/staff-session/staff-session.context";
import ThemeContextProvider from "@/contexts/theme/theme.context-provider";
import useOnLogout from "@/hooks/use-on-logout.hook";
import i18next from "@/i18n";
import CriticalError from "@/routes/@errors/500";
import Auth from "@/routes/auth";
import queryClient from "@/services/config/query-client";
import { handleError } from "@/services/helpers/clicknpark-errors.helpers";
import { Button, Spinner } from "@EspaceParkQC/sdk-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { I18nextProvider, useTranslation } from "react-i18next";
import { RouterProvider } from "react-router-dom";
import { appRouter } from "./router";

// Removes auto-zoom on iPhone
if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
  const viewport = document.querySelector('meta[name="viewport"]');
  viewport?.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1");
}

/** Staff session: the user comes from the clicknpark API, and requests read the staff token themselves. */
function StaffComponent(): React.ReactElement {
  const logout = useOnLogout();
  const { t } = useTranslation("main");
  const { user, error } = useStaffSession();

  if (error) {
    const handled = handleError(error);
    return (
      <CriticalError
        title={handled.formatted.title}
        description={handled.formatted.description}
        action={<Button onClick={() => void logout()}>{t("session.logout")}</Button>}
      />
    );
  }

  if (!user) {
    return (
      <Wrapper className="items-center justify-center gap-4">
        <Spinner className="text-primary" />
        <p>{t("session.loading-user-info")}</p>
      </Wrapper>
    );
  }

  return <RouterProvider router={appRouter} />;
}

function SessionSwitch(): React.ReactElement {
  const { mode } = useStaffSession();
  const { t } = useTranslation("main");

  if (mode === "checking") {
    return (
      <Wrapper className="items-center justify-center gap-4">
        <Spinner className="text-primary" />
        <p>{t("session.authenticating")}</p>
      </Wrapper>
    );
  }

  return mode === "staff" ? <StaffComponent /> : <Auth />;
}

export default function Root(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <I18nextProvider i18n={i18next}>
          <ThemeContextProvider>
            <StaffSessionProvider>
              <SessionSwitch />
            </StaffSessionProvider>
            <DevTools />
          </ThemeContextProvider>
        </I18nextProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}
